import { Download, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { buyerStatement, type DomainPayment, type DomainSale } from '@dairy/domain';
import { addDays } from '@dairy/shared';
import { useIsOwner, useSession } from '@/auth/useSession';
import { DateField } from '@/components/DateField';
import { toast } from '@/components/toast';
import { Badge, Button, Card, CardTitle, Field, PageHeader, Table, Td, Th, buttonVariants } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { useToday } from '@/db/hooks';
import { downloadFile } from '@/lib/api';
import { useFormat } from '@/lib/format';
import { useBuyerLedgers } from './useMoney';

/** Screen 15 — Buyer detail (IN-4): unpaid sales with ages and buckets; statement for a date range. */
export function BuyerDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const f = useFormat();
  const isOwner = useIsOwner();
  const locale = useSession().profile?.locale ?? 'am';
  const today = useToday();
  const all = useBuyerLedgers();
  const entry = all?.find((x) => x.buyer.id === id);
  const [from, setFrom] = useState(addDays(today, -30));
  const [to, setTo] = useState(today);
  if (!entry) return null;
  const { buyer, ledger, sales, payments } = entry;
  const st = buyerStatement(sales as unknown as DomainSale[], payments as unknown as DomainPayment[], from, to);
  return (
    <div className="space-y-4">
      <PageHeader
        title={buyer.name}
        actions={
          isOwner ? (
            <Link to={`/buyers/${id}/edit`} className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
              <Pencil className="h-4 w-4" /> {t('common.edit')}
            </Link>
          ) : null
        }
      />
      <div className="flex flex-wrap gap-2">
        <Link to={`/sales/new?buyerId=${id}`} className={buttonVariants({ size: 'sm' })}>
          {t('money.recordSale')}
        </Link>
        <Link to={`/payments/new?buyerId=${id}`} className={buttonVariants({ size: 'sm', variant: 'secondary' })}>
          {t('money.recordPayment')}
        </Link>
      </div>
      <Card>
        <div className="flex items-baseline justify-between">
          <span className="text-stone-600">{t('money.balance')}</span>
          <span className={`text-2xl font-bold tabular-nums ${ledger.balanceSantim < 0 ? 'text-green-800' : ''}`}>
            {ledger.balanceSantim < 0 ? `${t('money.credit')} ${f.birr(-ledger.balanceSantim)}` : f.birr(ledger.balanceSantim)}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-2 text-center text-sm">
          {(['0-7', '8-30', '31-60', '60+'] as const).map((b) => (
            <div key={b} className="rounded-xl bg-stone-100 p-2">
              <div className="text-stone-600">{t(`money.bucket.${b}`)}</div>
              <div className="font-semibold tabular-nums">{f.amount(ledger.buckets[b])}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <CardTitle>{t('money.unpaidSales')}</CardTitle>
        <Table>
          <thead>
            <tr>
              <Th>{t('common.date')}</Th>
              <Th className="text-right">{t('money.amount')}</Th>
              <Th className="text-right">{t('money.unpaid')}</Th>
              <Th className="text-right">{t('money.ageDays')}</Th>
            </tr>
          </thead>
          <tbody>
            {ledger.unpaid.map((u) => (
              <tr key={u.saleId}>
                <Td>{f.short(u.date)}</Td>
                <Td className="text-right tabular-nums">{f.amount(u.amountSantim)}</Td>
                <Td className="text-right tabular-nums">{f.amount(u.unpaidSantim)}</Td>
                <Td className="text-right">
                  {u.ageDays} {u.overdue ? <Badge tone="red">{t('money.overdueBadge')}</Badge> : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      <Card>
        <CardTitle>{t('money.statement')}</CardTitle>
        <div className="mb-3 grid gap-3 sm:grid-cols-2">
          <Field label={t('common.from')}>
            <DateField value={from} max={today} onChange={setFrom} />
          </Field>
          <Field label={t('common.to')}>
            <DateField value={to} max={today} onChange={setTo} />
          </Field>
        </div>
        <Table>
          <tbody>
            <tr>
              <Td colSpan={3} className="font-semibold">
                {t('money.openingBalance')}
              </Td>
              <Td className="text-right font-semibold tabular-nums">{f.amount(st.openingSantim)}</Td>
            </tr>
            {st.lines.map((l) => (
              <tr key={l.id}>
                <Td>{f.short(l.date)}</Td>
                <Td>{l.kind === 'SALE' ? t('money.sale') : t('money.payment')}</Td>
                <Td className="text-right tabular-nums">{l.kind === 'SALE' ? f.amount(l.amountSantim) : `−${f.amount(l.amountSantim)}`}</Td>
                <Td className="text-right tabular-nums">{f.amount(l.runningSantim)}</Td>
              </tr>
            ))}
            <tr>
              <Td colSpan={3} className="font-semibold">
                {t('money.closingBalance')}
              </Td>
              <Td className="text-right font-semibold tabular-nums">{f.amount(st.closingSantim)}</Td>
            </tr>
          </tbody>
        </Table>
        {isOwner ? (
          <Button
            variant="secondary"
            className="mt-3"
            onClick={() =>
              downloadFile(`/exports/buyers/${id}/statement?from=${from}&to=${to}&format=pdf&lang=${locale}`, `statement-${from}-${to}.pdf`).catch((e) =>
                toast(errorMessage(t, e), 'error'),
              )
            }
          >
            <Download className="h-4 w-4" /> {t('reports.downloadPdf')}
          </Button>
        ) : null}
      </Card>
    </div>
  );
}
